import {
  DoBoxEntryKind,
  DoBoxSpecialBucketKey,
  InboxEntry,
} from '@this+that/models';

import type { DoBoxBucketLimitInput } from '../../../../services/inboxEntries.service';
import { BucketKey } from '../../../../store/Inbox/InboxEntries/types';
import { DOBOX_PAGE_LIMIT } from '../../../../utils/constants';

export const DOBOX_REFRESH_MAX_LIMIT = 100;

interface BuildDoBoxRefreshBucketLimitsParams {
  inboxEntries: InboxEntry[];
  knownSubgroupIds: string[];
}

export function buildDoBoxRefreshBucketLimits({
  inboxEntries,
  knownSubgroupIds,
}: BuildDoBoxRefreshBucketLimitsParams): DoBoxBucketLimitInput[] {
  const knownSubgroupIdSet = new Set(knownSubgroupIds);
  const countsByBucket = countLoadedEntriesByBucket(
    inboxEntries,
    knownSubgroupIdSet,
  );

  return Array.from(countsByBucket.entries()).map(
    ([bucketKey, loadedCount]) => ({
      bucketKey,
      limit: resolveRefreshLimit(loadedCount),
    }),
  );
}

function countLoadedEntriesByBucket(
  inboxEntries: InboxEntry[],
  knownSubgroupIdSet: Set<string>,
): Map<BucketKey, number> {
  const counts = new Map<BucketKey, number>();
  for (const entry of inboxEntries) {
    const bucketKey = resolveEntryBucketKey(entry, knownSubgroupIdSet);
    counts.set(bucketKey, (counts.get(bucketKey) ?? 0) + 1);
  }
  return counts;
}

function resolveEntryBucketKey(
  entry: InboxEntry,
  knownSubgroupIdSet: Set<string>,
): BucketKey {
  if (entry.entryKind === DoBoxEntryKind.APPROVAL) {
    return DoBoxSpecialBucketKey.APPROVALS;
  }
  if (entry.isSuggestion === true) {
    return DoBoxSpecialBucketKey.SUGGESTIONS;
  }
  if (entry.taskSubgroupId && knownSubgroupIdSet.has(entry.taskSubgroupId)) {
    return entry.taskSubgroupId;
  }
  return DoBoxSpecialBucketKey.BACKLOG;
}

function resolveRefreshLimit(loadedCount: number): number {
  return Math.min(
    Math.max(loadedCount, DOBOX_PAGE_LIMIT),
    DOBOX_REFRESH_MAX_LIMIT,
  );
}
